import { Drawer, List, ListItemButton, ListItemIcon, ListItemText, useTheme } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { SidebarItems, type SidebarItem } from './SidebarItems';
import { useSelector } from 'react-redux';
import { useFiltersDispatch } from '../TrackerFilter/Context/FiltersContext';
import React, { type CSSProperties } from 'react';
import { getSidebarItemAddonControlIfExists } from '../../utils/RouteAddonControlUtils';
import { logPalette } from '../../theme/palette';

export const SIDEBAR_WIDTH = 250;

const getSidebarItemSx = (contrastText: string) => ({
  height: '60px',
  px: '16px',
  pl: '12px',
  width: '100%',
  listStyle: 'none',
  m: 0,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: '10px',
  boxSizing: 'border-box',
  color: '#111111',
  '&:hover': {
    cursor: 'pointer',
  },
  '&:hover, &.Mui-selected': {
    backgroundColor: logPalette.service.dark,
    color: contrastText,
  },
  '& .sidebar-item-icon': {
    color: 'var(--sidebar-item-icon-color, #111111)',
    minWidth: 28,
    width: 28,
    height: 28,
    marginRight: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 120ms ease-in-out',
  },
  '& .sidebar-item-icon .MuiSvgIcon-root': {
    fontSize: '20px',
  },
  '& .sidebar-item-icon svg': {
    width: 20,
    height: 20,
    display: 'block',
  },
  '&:hover .sidebar-item-icon, &.Mui-selected .sidebar-item-icon': {
    color: contrastText,
  },
  '& .MuiListItemText-root': {
    margin: 0,
    minWidth: 0,
  },
  '& .MuiListItemText-primary': {
    color: '#111111',
    fontFamily: '"Open Sans", Arial, sans-serif',
    fontSize: '0.9rem',
    fontWeight: 500,
    lineHeight: 1.2,
    transition: 'color 120ms ease-in-out',
  },
  '&:hover .MuiListItemText-primary, &.Mui-selected .MuiListItemText-primary': {
    color: contrastText,
  },
});

type SidebarProps = {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  isMobile: boolean;
};

function Sidebar(props: Readonly<SidebarProps>) {
  const { pathname } = useLocation();

  const theme = useTheme();
  const sidebarItemSx = getSidebarItemSx(theme.palette.primary.contrastText);
  const { open, setOpen, isMobile } = props;

  const user = useSelector((state: any) => state.user.userInfo) as { privilege: number };
  const userAddonControl = useSelector((state: any) => state.user.addonControls) as Record<string, unknown>;
  const dispatch = useFiltersDispatch();
  const items = SidebarItems.map((val: SidebarItem) => {
    const itemIconStyle = { '--sidebar-item-icon-color': val.iconColor ?? '#111111' } as CSSProperties;
    const itemKey = val.link;
    const itemContents = (
      <>
        <ListItemIcon className="sidebar-item-icon">{val.icon}</ListItemIcon>
        <ListItemText primary={val.title} />
      </>
    );
    const isSelected = pathname === val.link;

    if (val.accessibility <= user.privilege && getSidebarItemAddonControlIfExists(val, userAddonControl)) {
      if (val.title === 'Feedback') {
        return (
          <ListItemButton
            key={itemKey}
            component="a"
            href={val.link}
            selected={isSelected}
            onClick={() => {
              dispatch({ type: 'setFilters', payload: {} });
              setOpen(false);
            }}
            style={itemIconStyle}
            sx={sidebarItemSx}
          >
            {itemContents}
          </ListItemButton>
        );
      } else {
        return (
          <ListItemButton
            key={itemKey}
            component={Link}
            to={val.link}
            selected={isSelected}
            onClick={() => {
              dispatch({ type: 'setFilters', payload: {} });
              setOpen(false);
            }}
            style={itemIconStyle}
            sx={sidebarItemSx}
          >
            {itemContents}
          </ListItemButton>
        );
      }
    } else {
      return null;
    }
  });

  return (
    <Drawer
      anchor="left"
      variant={isMobile ? 'temporary' : 'persistent'}
      open={open}
      onClose={() => setOpen(false)}
      PaperProps={{
        sx: {
          top: 60,
          width: SIDEBAR_WIDTH,
        },
      }}
      SlideProps={{ timeout: 200 }}
      ModalProps={{ keepMounted: true }}
      sx={{
        height: '100%',
        padding: 0,
        width: '100%',
      }}
      data-cy="SideBar-Drawer"
    >
      <List sx={{ height: '100%', p: 0, width: '100%' }}>{items}</List>
    </Drawer>
  );
}

export default Sidebar;
